var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import getMessage from 'src/background/utils/i18n.utils';
import { joiResolver } from '@hookform/resolvers/joi';
import { KeychainKeyTypes, KeychainKeyTypesLC, } from '@interfaces/keychain.interface';
import { addToLoadingList, removeFromLoadingList, } from '@popup/multichain/actions/loading.actions';
import { setErrorMessage, setSuccessMessage, } from '@popup/multichain/actions/message.actions';
import { goBack, navigateTo, navigateToWithParams, } from '@popup/multichain/actions/navigation.actions';
import { setTitleContainerProperties } from '@popup/multichain/actions/title-container.actions';
import { fetchPhishingAccounts } from '@popup/steem/actions/phishing.actions';
import AccountUtils from '@popup/steem/utils/account.utils';
import CurrencyUtils from '@popup/steem/utils/currency.utils';
import { FavoriteUserUtils } from '@popup/steem/utils/favorite-user.utils';
import { KeysUtils } from '@popup/steem/utils/keys.utils';
import TokensUtils from '@popup/steem/utils/tokens.utils';
import Decimal from 'decimal.js';
import Joi from 'joi';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { BalanceSectionComponent } from 'src/common-ui/balance-section/balance-section.component';
import { OperationButtonComponent } from 'src/common-ui/button/operation-button.component';
import { FormContainer } from 'src/common-ui/form-container/form-container.component';
import { SVGIcons } from 'src/common-ui/icons.enum';
import { FormInputComponent } from 'src/common-ui/input/form-input.component';
import { InputType } from 'src/common-ui/input/input-type.enum';
import { Screen } from 'src/reference-data/screen.enum';
import { FormUtils } from 'src/utils/form.utils';
import FormatUtils from 'src/utils/format.utils';
export var TokenOperationType;
(function (TokenOperationType) {
    TokenOperationType["STAKE"] = "stake";
    TokenOperationType["UNSTAKE"] = "unstake";
    TokenOperationType["DELEGATE"] = "delegate";
})(TokenOperationType || (TokenOperationType = {}));
const tokenOperationRules = FormUtils.createRules({
    receiverUsername: Joi.string().required(),
    amount: Joi.number().required().positive().max(Joi.ref('$balance')),
});
const TokensOperation = ({ operationType, activeAccount, tokenBalance, tokenInfo, formParams, localAccounts, setErrorMessage, setSuccessMessage, navigateToWithParams, navigateTo, addToLoadingList, removeFromLoadingList, setTitleContainerProperties, goBack, pendingUnstaking, }) => {
    const [balance, setBalance] = useState();
    const { control, handleSubmit, setValue, watch } = useForm({
        defaultValues: {
            receiverUsername: formParams.receiverUsername
                ? formParams.receiverUsername
                : operationType === TokenOperationType.DELEGATE
                    ? ''
                    : activeAccount.name,
            amount: formParams.amount ? formParams.amount : '',
            symbol: formParams.symbol ? formParams.symbol : tokenBalance.symbol,
        },
        resolver: (values, context, options) => {
            const resolver = joiResolver(tokenOperationRules, { context: { balance: balance }, errors: { render: true } });
            return resolver(values, { balance: balance }, options);
        },
    });
    const [autocompleteFavoriteUsers, setAutocompleteFavoriteUsers] = useState({ categories: [] });
    useEffect(() => {
        setTitleContainerProperties({
            title: `popup_html_${operationType}_tokens`,
            isBackButtonEnabled: true,
        });
        loadAutocompleteFavoriteUsers();
        switch (operationType) {
            case TokenOperationType.UNSTAKE:
            case TokenOperationType.DELEGATE:
                const tokenUnstaking = pendingUnstaking.filter((e) => e.symbol === tokenBalance.symbol);
                const sumUnstaking = tokenUnstaking.reduce((a, b) => Decimal.sub(a, Decimal.div(parseFloat(b.quantity), tokenInfo.numberTransactions))
                    .add(parseFloat(b.quantityLeft))
                    .toNumber(), 0);
                const available = Decimal.sub(parseFloat(tokenBalance.stake), sumUnstaking)
                    .times(Decimal.pow(10, tokenInfo.precision))
                    .floor()
                    .div(Decimal.pow(10, tokenInfo.precision))
                    .toNumber();
                setBalance(available);
                break;
            case TokenOperationType.STAKE:
                setBalance(parseFloat(tokenBalance.balance));
                break;
        }
    }, []);
    const loadAutocompleteFavoriteUsers = () => __awaiter(void 0, void 0, void 0, function* () {
        setAutocompleteFavoriteUsers(yield FavoriteUserUtils.getAutocompleteListByCategories(activeAccount.name, localAccounts));
    });
    const setAmountToMaxValue = () => {
        setValue('amount', parseFloat(balance.toFixed(tokenInfo.precision)));
    };
    const getFormParams = () => {
        return watch();
    };
    const handleClickOnSend = (form) => __awaiter(void 0, void 0, void 0, function* () {
        if (!(yield AccountUtils.doesAccountExist(form.receiverUsername))) {
            setErrorMessage('popup_no_such_account');
            return;
        }
        if (parseFloat(form.amount.toString()) <= 0) {
            setErrorMessage('popup_html_need_positive_amount');
            return;
        }
        if (parseFloat(form.amount.toString()) > parseFloat(balance.toString())) {
            setErrorMessage('popup_html_power_up_down_error');
            return;
        }
        const formattedAmount = `${parseFloat(form.amount.toString()).toFixed(8)} ${form.symbol}`;
        const stringifiedAmount = `${FormatUtils.formatCurrencyValue(parseFloat(form.amount.toString()), tokenInfo.precision)} ${form.symbol}`;
        const fields = [
            { label: 'popup_html_transfer_amount', value: stringifiedAmount },
        ];
        if (operationType === TokenOperationType.DELEGATE) {
            fields.unshift({
                label: 'popup_html_transfer_to',
                value: `@${form.receiverUsername}`,
            });
        }
        navigateToWithParams(Screen.CONFIRMATION_PAGE, {
            method: KeychainKeyTypes.active,
            message: getMessage(`popup_html_${operationType}_tokens_confirm_text`),
            fields: fields,
            title: `popup_html_${operationType}_tokens`,
            formParams: getFormParams(),
            afterConfirmAction: (options) => __awaiter(void 0, void 0, void 0, function* () {
                addToLoadingList(`popup_html_${operationType}_tokens`, KeysUtils.getKeyType(activeAccount.keys.active, activeAccount.keys.activePubkey));
                let tokenOperationResult;
                try {
                    switch (operationType) {
                        case TokenOperationType.DELEGATE:
                            tokenOperationResult = yield TokensUtils.delegateToken(form.receiverUsername, form.symbol, form.amount.toString(), activeAccount.keys.active, activeAccount.name, options);
                            break;
                        case TokenOperationType.STAKE:
                            tokenOperationResult = yield TokensUtils.stakeToken(form.receiverUsername, form.symbol, form.amount.toString(), activeAccount.keys.active, activeAccount.name, options);
                            break;
                        case TokenOperationType.UNSTAKE:
                            tokenOperationResult = yield TokensUtils.unstakeToken(form.symbol, form.amount.toString(), activeAccount.keys.active, activeAccount.name, options);
                            break;
                    }
                    if (tokenOperationResult && tokenOperationResult.isUsingMultisig) {
                        navigateTo(Screen.HOME_PAGE, true);
                        setSuccessMessage('multisig_transaction_sent_to_signers');
                    }
                    else if (tokenOperationResult && tokenOperationResult.broadcasted) {
                        addToLoadingList('html_popup_confirm_transaction_operation');
                        removeFromLoadingList(`popup_html_${operationType}_tokens`);
                        removeFromLoadingList('html_popup_confirm_transaction_operation');
                        if (tokenOperationResult.confirmed) {
                            yield FavoriteUserUtils.saveFavoriteUser(form.receiverUsername, activeAccount);
                            setSuccessMessage(`popup_html_${operationType}_tokens_success`);
                            navigateTo(Screen.HOME_PAGE, true);
                        }
                        else {
                            setErrorMessage('popup_token_timeout');
                        }
                    }
                    else {
                        removeFromLoadingList(`popup_html_${operationType}_tokens`);
                        setErrorMessage(`popup_html_${operationType}_tokens_failed`);
                    }
                }
                catch (err) {
                    setErrorMessage(err.message, [err]);
                }
                finally {
                    removeFromLoadingList('html_popup_confirm_transaction_operation');
                    removeFromLoadingList(`popup_html_${operationType}_tokens`);
                }
            }),
        });
    });
    const getSubmitButtonLabel = () => {
        switch (operationType) {
            case TokenOperationType.DELEGATE:
                return 'popup_html_token_delegate';
            case TokenOperationType.STAKE:
                return 'popup_html_token_stake';
            case TokenOperationType.UNSTAKE:
                return 'popup_html_token_unstake';
            default:
                return 'popup_html_send_transfer';
        }
    };
    return (React.createElement("div", { "data-testid": `${Screen.TOKENS_OPERATION}-page`, className: "tokens-operation-page" },
        balance !== undefined && (React.createElement(BalanceSectionComponent, { value: balance, unit: watch('symbol'), label: "popup_html_balance", decimals: tokenInfo.precision })),
        React.createElement("div", { className: "caption" }, getMessage('popup_html_tokens_operation_text')),
        operationType === TokenOperationType.UNSTAKE &&
            tokenInfo.unstakingCooldown > 0 && (React.createElement("div", { className: "cooldown-message" }, getMessage('popup_html_token_unstake_cooldown_disclaimer', [tokenInfo.unstakingCooldown.toString()]))),
        React.createElement(FormContainer, { onSubmit: handleSubmit(handleClickOnSend) },
            React.createElement("div", { className: "form-fields" },
                operationType === TokenOperationType.DELEGATE && (React.createElement(FormInputComponent, { control: control, name: "receiverUsername", dataTestId: "input-username", type: InputType.TEXT, logo: SVGIcons.INPUT_AT, placeholder: "popup_html_username", autocompleteValues: autocompleteFavoriteUsers })),
                React.createElement("div", { className: "value-panel" },
                    React.createElement(FormInputComponent, { classname: "currency-fake-input", dataTestId: "currency-input", control: control, name: "symbol", type: InputType.TEXT, label: "popup_html_currency", disabled: true }),
                    React.createElement(FormInputComponent, { dataTestId: "amount-input", name: "amount", control: control, type: InputType.NUMBER, min: 0, placeholder: "0.000", label: "popup_html_transfer_amount", skipPlaceholderTranslation: true, rightActionClicked: setAmountToMaxValue, rightActionIcon: SVGIcons.INPUT_MAX }))),
            React.createElement(OperationButtonComponent, { dataTestId: `token-button-operation-${operationType}`, requiredKey: KeychainKeyTypesLC.active, label: getSubmitButtonLabel(), onClick: handleSubmit(handleClickOnSend) }))));
};
const mapStateToProps = (state) => {
    var _a, _b, _c, _d, _e;
    return {
        activeAccount: state.steem.activeAccount,
        currencyLabels: CurrencyUtils.getCurrencyLabels((_a = state.steem.activeRpc) === null || _a === void 0 ? void 0 : _a.testnet),
        tokenBalance: (_b = state.navigation.stack[0].params) === null || _b === void 0 ? void 0 : _b.tokenBalance,
        tokenInfo: state.navigation.stack[0].params.tokenInfo,
        operationType: (_c = state.navigation.stack[0].params) === null || _c === void 0 ? void 0 : _c.operationType,
        formParams: ((_d = state.navigation.stack[0].previousParams) === null || _d === void 0 ? void 0 : _d.formParams)
            ? (_e = state.navigation.stack[0].previousParams) === null || _e === void 0 ? void 0 : _e.formParams
            : {},
        phishing: state.steem.phishing,
        localAccounts: state.steem.accounts,
        pendingUnstaking: state.steem.tokensPendingUnstaking,
    };
};
const connector = connect(mapStateToProps, {
    setErrorMessage,
    setSuccessMessage,
    navigateToWithParams,
    navigateTo,
    fetchPhishingAccounts,
    addToLoadingList,
    removeFromLoadingList,
    setTitleContainerProperties,
    goBack,
});
export const TokensOperationComponent = connector(TokensOperation);
